import { useMemo } from 'react';
import useMediaQuery from './useMediaQuery';

/**
 * Breakpoints définis pour l'application
 * Mobile-first approach
 */
export const BREAKPOINTS = {
  // Breakpoints principaux
  mobile: '(max-width: 767px)',
  tablet: '(min-width: 768px) and (max-width: 1024px)',
  desktop: '(min-width: 1025px)',
  largeDesktop: '(min-width: 1440px)',

  // Breakpoints spécifiques
  smallMobile: '(max-width: 480px)',
  largeMobile: '(min-width: 481px) and (max-width: 767px)',

  // Pour les media queries max-width (fallback)
  maxMobile: '(max-width: 767px)',
  maxTablet: '(max-width: 1024px)',
} as const;

/**
 * Valeurs numériques des breakpoints pour usage en JavaScript
 */
export const BREAKPOINT_VALUES = {
  smallMobile: 480,
  mobile: 767,
  tablet: 1024,
  desktop: 1025,
  largeDesktop: 1440,
} as const;

/**
 * Hook pour gérer la responsivité de l'application
 * @returns Objet contenant les états responsive et utilitaires
 *
 * @example
 * const { isMobile, isTablet, getResponsiveValue } = useResponsive();
 * const padding = getResponsiveValue({ mobile: 16, tablet: 24, desktop: 40 });
 */
export const useResponsive = () => {
  const isMobile = useMediaQuery(BREAKPOINTS.mobile);
  const isTablet = useMediaQuery(BREAKPOINTS.tablet);
  const isDesktop = useMediaQuery(BREAKPOINTS.desktop);
  const isLargeDesktop = useMediaQuery(BREAKPOINTS.largeDesktop);
  const isSmallMobile = useMediaQuery(BREAKPOINTS.smallMobile);

  // Utilitaires dérivés
  const isMobileOrTablet = useMemo(() => isMobile || isTablet, [isMobile, isTablet]);
  const isTabletOnly = useMemo(() => isTablet, [isTablet]);
  const isDesktopOrLarger = useMemo(() => isDesktop || isLargeDesktop, [isDesktop, isLargeDesktop]);

  /**
   * Retourne une valeur selon le breakpoint actuel
   * @example getResponsiveValue({ mobile: 16, tablet: 24, desktop: 40 })
   */
  const getResponsiveValue = useMemo(
    () =>
      <T>(values: {
        mobile?: T;
        tablet?: T;
        desktop?: T;
        largeDesktop?: T;
        default?: T;
      }): T | undefined => {
        if (isLargeDesktop && values.largeDesktop !== undefined) return values.largeDesktop;
        if (isDesktop && values.desktop !== undefined) return values.desktop;
        if (isTablet && values.tablet !== undefined) return values.tablet;
        if (isMobile && values.mobile !== undefined) return values.mobile;
        return values.default;
      },
    [isMobile, isTablet, isDesktop, isLargeDesktop],
  );

  /**
   * Retourne le nom du breakpoint actuel
   */
  const currentBreakpoint = useMemo(() => {
    if (isLargeDesktop) return 'largeDesktop';
    if (isDesktop) return 'desktop';
    if (isTablet) return 'tablet';
    if (isMobile) return 'mobile';
    return 'unknown';
  }, [isMobile, isTablet, isDesktop, isLargeDesktop]);

  return {
    // États de base
    isMobile,
    isTablet,
    isDesktop,
    isLargeDesktop,
    isSmallMobile,

    // Utilitaires
    isMobileOrTablet,
    isTabletOnly,
    isDesktopOrLarger,

    // Helpers
    getResponsiveValue,
    currentBreakpoint,

    // Breakpoints pour usage direct
    breakpoints: BREAKPOINTS,
    breakpointValues: BREAKPOINT_VALUES,
  };
};
